import { useState } from 'react';
import { Avatars, Chip } from '../../components/bits';
import { useStore } from '../../state/store';
import { teamHealth, todayCounts } from '../../lib/derive';

/** “Salud de equipos”: la constelación. El sol es el día; cada órbita, un equipo. */
export function HealthView() {
  const s = useStore();
  const snap = s.snapshot;
  const health = teamHealth(snap);
  const counts = todayCounts(snap);
  const [sel, setSel] = useState<string>(health.find(h => h.issues.length)?.team.id ?? health[0].team.id);
  const W = 800, H = 540, cx = W / 2, cy = H / 2;
  const pos = [[150, 120], [650, 120], [150, 420], [650, 420]];
  const selected = health.find(h => h.team.id === sel)!;

  return (
    <div className="row wrap" style={{ alignItems: 'flex-start', gap: 16 }}>
      <section className="card" style={{ flex: '999 1 640px', minWidth: 0, padding: 14, overflowX: 'auto', background: 'radial-gradient(circle at 50% 50%, #FFFDF6, #FFFFFF 60%)' }}>
        <div style={{ position: 'relative', width: W, height: H, margin: '0 auto' }}>
          <svg width={W} height={H} style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
            {health.map((h, i) => {
              const [x, y] = pos[i % 4];
              const bad = h.issues.length > 0;
              return (
                <g key={h.team.id}>
                  <line x1={cx} y1={cy} x2={x} y2={y} stroke={bad ? '#E5484D' : '#CFCFCB'} strokeWidth={bad ? 2 : 1.5} strokeDasharray={bad ? '6 5' : undefined} />
                  <circle cx={x} cy={y} r={82} fill="none" stroke="#E2E2DF" strokeDasharray="3 5" />
                </g>
              );
            })}
          </svg>
          <div style={{ position: 'absolute', left: cx, top: cy, transform: 'translate(-50%,-50%)', width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%, #F4ED22, #FAAF3B 70%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 14px rgba(250,175,59,.15), 0 0 0 34px rgba(250,175,59,.07)' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Hoy</span><b style={{ fontSize: 40, lineHeight: 1 }}>{counts.total}</b>
            <span style={{ fontSize: 12, fontWeight: 600 }}>trabajos · {counts.running} activos</span>
          </div>
          {health.map((h, i) => {
            const [x, y] = pos[i % 4];
            const vs = snap.visits.filter(v => v.date === snap.today && v.teamId === h.team.id);
            const bad = h.issues.length > 0;
            return (
              <div key={h.team.id}>
                {vs.map((v, k) => {
                  const ang = -Math.PI / 2 + ((k + 0.5) * 2 * Math.PI) / vs.length + 0.6;
                  const c = v.risk ? '#E5484D' : v.status === 'completed' ? '#3A3A3D' : v.status === 'inProgress' ? '#2563EB' : '#FAAF3B';
                  const sz = v.risk ? 24 : 16;
                  return <span key={v.uid} style={{ position: 'absolute', left: x + 82 * Math.cos(ang), top: y + 82 * Math.sin(ang), width: sz, height: sz, borderRadius: '50%', background: c, transform: 'translate(-50%,-50%)', boxShadow: v.risk ? '0 0 0 5px rgba(229,72,77,.18)' : undefined }} />;
                })}
                <button type="button" onClick={() => setSel(h.team.id)}
                  style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%,-50%)', width: 130, background: '#fff', border: `2px solid ${bad ? '#E5484D' : sel === h.team.id ? '#1F1F21' : h.team.color}`, borderRadius: 14, padding: '8px 6px', textAlign: 'center', cursor: 'pointer', font: 'inherit' }}>
                  <b>{h.team.name}</b><br />
                  <span className="muted" style={{ fontSize: 12 }}>{Math.round(h.occupancy * 100)}% lleno</span><br />
                  <b style={{ fontSize: 12, color: bad ? '#B91C1C' : '#166534' }}>{bad ? `${h.issues.length} ${h.issues.length === 1 ? 'problema' : 'problemas'}` : 'Todo bien'}</b>
                </button>
              </div>
            );
          })}
        </div>
      </section>
      <aside style={{ flex: '1 1 300px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <section className="w">
          <div className="row"><span style={{ width: 12, height: 12, borderRadius: 4, background: selected.team.color }} /><h3 style={{ margin: 0, fontSize: 16 }}>{selected.team.name}</h3><Avatars team={selected.team} color={selected.team.color} size={22} /></div>
          {selected.issues.length === 0 && <Chip bg="#DCFCE7" fg="#166534" style={{ alignSelf: 'flex-start' }}>Sin problemas hoy</Chip>}
          {selected.issues.map(i => <div key={i} style={{ background: '#FEE2E2', color: '#7F1D1D', borderRadius: 10, padding: 10, fontSize: 13, fontWeight: 600 }}>{i}</div>)}
          {selected.notes.map(n => <div key={n} className="muted" style={{ border: '1.5px dashed #C9C8C2', borderRadius: 10, padding: 8, fontSize: 12 }}>{n}</div>)}
          <button type="button" className="btn" onClick={() => s.setAgendaMode('calendario')}>Ver en calendario</button>
        </section>
        <section className="w" style={{ gap: 8, fontSize: 13 }}>
          <b>Cómo leerla</b>
          <span className="muted">El sol es tu día; cada órbita, un equipo; cada planeta, un trabajo. Rojo = requiere atención.</span>
          <div className="row wrap" style={{ gap: 10, fontSize: 12 }}>
            {[['#3A3A3D', 'Hecho'], ['#2563EB', 'En curso'], ['#FAAF3B', 'Programado'], ['#E5484D', 'Atención']].map(([c, l]) => <span key={l} className="row" style={{ gap: 5 }}><i style={{ width: 12, height: 12, borderRadius: '50%', background: c }} />{l}</span>)}
          </div>
        </section>
      </aside>
    </div>
  );
}
